import type { HelpArticle } from "../../types";
import { W3_LOADS } from "../../../calculations/calcPreviewFixtures";

const lb = (value: number): string => `${value.toLocaleString()} lb`;

export const memberLoadsArticle: HelpArticle = {
  id: "member-loads",
  title: "Applying loads to members",
  description: "Enter, edit, group, and review full-span, partial, tapered, and point loads on wood members, including the current unsupported settings.",
  tags: ["wood", "loads", "point load", "partial load", "load case", "self-weight"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Card B is the sheet's explicit load ledger. Each row keeps its ASCE load type, application geometry, magnitude, position or extent, and label. The applied-load diagram reads the same rows, so the member sketch and the editable ledger describe one stored state rather than parallel interpretations.",
    },

    { kind: "heading", level: 2, text: "Load units follow the role", id: "role-units" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", W3_LOADS.mark, "B. Loads"] },
    {
      kind: "paragraph",
      text: "Spaced roles such as floor joists and rafters accept distributed area loads in psf; their spacing supplies the automatic tributary width used by the adapter. Beam, header, and ridge roles accept distributed line loads in plf. Point loads are entered in lb at a station in feet. Axial roles use force rows instead of beam-distribution geometry, and beam-column roles also expose entered moments for their declared axes.",
    },
    {
      kind: "callout",
      variant: "info",
      title: "Rows remain service-level load types",
      body: "D, L, Lr, S, R, and wind rows remain separated. The engine forms the applicable combinations and returns the governing one; do not pre-combine unrelated load types into a single row when their currency needs to remain traceable.",
    },

    { kind: "heading", level: 2, text: "Four beam application geometries", id: "geometries" },
    {
      kind: "definition-list",
      items: [
        { term: "Full span", definition: "A constant distributed load over the member's modeled span. The row needs a magnitude but no start or end station." },
        { term: "Partial span", definition: "A constant distributed load between entered start and end stations. The inline row keeps both extents visible." },
        { term: "Tapered", definition: "A linearly varying distributed load between start and end. The editor records intensities at both ends." },
        { term: "Point", definition: "A concentrated load in lb at one position measured from the left end." },
      ],
    },
    {
      kind: "paragraph",
      text: "Card B groups rows by application geometry rather than mixing every station and unit in one table. Each group has its own Add action. A full-span row therefore stays compact, while partial and point rows expose the stations that distinguish them. Empty groups explain what belongs there instead of manufacturing zero-valued loads.",
    },
    {
      kind: "shot",
      shotId: "wood-loads-applied",
      caption: "R-3's applied-load diagram shows the converted distributed rows and the full 1,000 lb point demand at the ridge.",
    },
    {
      kind: "values",
      caption: `${W3_LOADS.mark}: load rows sent by the deterministic fixture.`,
      rows: [
        { label: "Dead", value: `${W3_LOADS.loads[0].mag} psf · ${W3_LOADS.loads[0].linePlf} plf at 24 in o.c. · full span` },
        { label: "Snow at ridge", value: `${lb(W3_LOADS.loads[1].mag)} · point at ${W3_LOADS.loads[1].pos} ft` },
        { label: "Roof live", value: `${W3_LOADS.loads[2].mag} psf · ${W3_LOADS.loads[2].linePlf} plf at 24 in o.c. · ${W3_LOADS.loads[2].start}–${W3_LOADS.loads[2].end} ft` },
      ],
    },

    { kind: "heading", level: 2, text: "Editing and commit behavior", id: "editing" },
    {
      kind: "steps",
      steps: [
        { title: "Add or open a row", body: "Use Add load, a group's + Add control, or the row's edit action. The modal previews the application on the current member before it is saved." },
        { title: "Choose the load case and geometry", body: "The chosen case controls the row's load-type currency. The chosen geometry controls which magnitude, extent, taper, or position inputs are required." },
        { title: "Name the source", body: "The optional label appears in the card ledger. Use it to distinguish sources that share a load case; the label does not change the calculation." },
        { title: "Commit the row", body: "Adding, duplicating, or removing commits immediately. Inline magnitude and station edits commit when focus leaves the field or when the sheet is recalculated." },
      ],
    },
    {
      kind: "shot",
      shotId: "wood-loads-groups",
      caption: "The distributed group keeps the full-span and partial rows' cases, labels, extents, magnitudes, units, and actions readable together.",
    },
    {
      kind: "shot",
      shotId: "wood-loads-editor",
      caption: "The load editor ties case, application geometry, magnitude, label, and position to one visible schematic.",
    },

    { kind: "heading", level: 2, text: "Self-weight, patterning, and linked rows", id: "settings" },
    {
      kind: "paragraph",
      text: "Include member self-weight asks the engine to add the selected section's stored weight; it is not a typed dead-load row and changes when a different section is seated. Automatic multi-span patterning becomes active only for a support layout with more than one span and eligible variable types L, Lr, S, or R. The badge reports whether that engine path is active. Brace-at-point-loads is visibly marked Engine pending and cannot be enabled.",
    },
    {
      kind: "paragraph",
      text: "A reaction linked from another sheet appears in a separate inherited block. It is read-only because its source member owns the value. The row keeps the source and support, per-load-type packet, and live or unresolved state visible; unlinking removes the graph edge rather than converting it into manual text.",
    },

    { kind: "heading", level: 2, text: "Current unsupported settings", id: "unsupported" },
    {
      kind: "callout",
      variant: "warning",
      title: "Projected alignment and manual tributary width do not compute",
      body: "The editor currently exposes projected/along-member alignment and a manual tributary-width override, but the adapter fails closed when either differs from the supported gravity/automatic-tributary path. Saving such a row can leave the sheet in an unsupported terminal state with no engine numbers. Do not treat those controls as calculated capability until the product disables them or the engine path lands.",
    },
    {
      kind: "paragraph",
      text: "This boundary matters most on sloped members: the editor's warning text refers to a gravity-vertical path, while the adapter refuses non-gravity alignment rather than silently converting it. The reliable record is the result state. If it says Engine support pending, no displayed demand or capacity should be inferred for that edit.",
    },

    { kind: "heading", level: 2, text: "Read the load path in the result", id: "result" },
    {
      kind: "values",
      caption: `${W3_LOADS.mark}: selected engine outputs for the three-row fixture.`,
      rows: [
        { label: "Governing", value: `${W3_LOADS.governing.label} · ${(W3_LOADS.governing.utilization * 100).toFixed(1)}% · ${W3_LOADS.governing.combo}` },
        { label: "Left reaction", value: `${lb(W3_LOADS.reactions[0].reactionLb)} · D ${lb(W3_LOADS.reactions[0].byType.D)} · Lr ${lb(W3_LOADS.reactions[0].byType.Lr)} · S ${lb(W3_LOADS.reactions[0].byType.S)}` },
        { label: "Right reaction", value: `${lb(W3_LOADS.reactions[1].reactionLb)} · D ${lb(W3_LOADS.reactions[1].byType.D)} · Lr ${lb(W3_LOADS.reactions[1].byType.Lr)} · S ${lb(W3_LOADS.reactions[1].byType.S)}` },
        { label: "Minimum bearing", value: `${W3_LOADS.reactions[0].minBearingIn.toFixed(3)} in at each support` },
      ],
    },
    {
      kind: "paragraph",
      text: "The equal reaction envelopes do not erase per-type asymmetry: R-3's partial roof-live row contributes 360 lb at the left and 120 lb at the right, while the centered point load contributes 500 lb to each. That per-type currency is what a downstream link carries; the envelope is a review summary, not the transfer payload.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Responsible-engineer load review",
      body: "The responsible engineer must review the selected load types and magnitudes, application geometry and extents, tributary-width basis, support idealization, applicable combinations, and the adequacy and completeness of the load path. Firmark records and computes supported inputs; its diagram and verdict do not establish that the declared loading model is adequate.",
    },

    { kind: "related", topics: ["linking-reactions", "member-geometry-supports", "design-conditions", "member-results"] },
  ],
};
